/**
 * SEO Content Data for BharatHeal
 * Comprehensive SEO-optimized content for all pages
 */

export const seoContent = {
  homepage: {
    title: "Medical Tourism India | Affordable Treatment | BharatHeal",
    description: "Get world-class medical treatment in India at 60-80% lower costs. Expert doctors, JCI-accredited hospitals, visa assistance. Trusted by 50K+ international patients.",
    keywords: "medical tourism in India, treatment in India for foreigners, affordable surgery in India, medical visa India, best hospitals in India, medical tourism, healthcare India, international patients India",
    hero: {
      headline: "World-Class Medical Treatment in India",
      subheadline: "Access affordable, high-quality healthcare from internationally accredited hospitals. Save 60-80% on medical costs while receiving world-class treatment.",
      valueProposition: "India's leading medical tourism platform connecting international patients with premier healthcare facilities. Expert visa assistance, hospital coordination, and end-to-end care support."
    },
    whyChooseUs: [
      {
        title: "Save 60-80% on Medical Costs",
        description: "Receive world-class medical treatment at a fraction of Western costs. India offers the same quality healthcare with significantly lower prices, making advanced treatments accessible to everyone.",
        icon: "DollarSign"
      },
      {
        title: "JCI-Accredited Hospitals",
        description: "Partner with internationally recognized hospitals meeting the highest global standards. Our network includes 50+ premier medical facilities with cutting-edge technology and infrastructure.",
        icon: "Building2"
      },
      {
        title: "Expert Medical Professionals",
        description: "Access to 200+ internationally trained doctors with decades of experience. Many specialists trained in the US, UK, and Europe, bringing world-class expertise to your care.",
        icon: "Stethoscope"
      },
      {
        title: "Complete Visa & Travel Support",
        description: "End-to-end assistance with medical visa processing, travel arrangements, airport pickup, and accommodation. We handle all logistics so you can focus on your health.",
        icon: "Plane"
      },
      {
        title: "24/7 Patient Support",
        description: "Dedicated multilingual support team available round-the-clock. From initial consultation to post-treatment follow-up, we're with you every step of your medical journey.",
        icon: "Phone"
      }
    ],
    trustSection: {
      title: "Trusted by Patients Worldwide",
      stats: [
        { value: "50K+", label: "Happy Patients" },
        { value: "200+", label: "Expert Doctors" },
        { value: "50+", label: "Partner Hospitals" },
        { value: "98%", label: "Success Rate" }
      ],
      certifications: [
        "JCI Accredited Hospitals",
        "ISO Certified Facilities",
        "NABH Certified Centers",
        "International Quality Standards"
      ]
    }
  },
  about: {
    title: "About Us | Medical Tourism India | BharatHeal",
    description: "Learn about BharatHeal - India's trusted medical tourism partner. Our mission, vision, and commitment to providing affordable, world-class healthcare to international patients.",
    keywords: "medical tourism company India, about BharatHeal, medical tourism services, healthcare India, international patient services",
    brandStory: {
      title: "Our Story",
      content: `BharatHeal was founded with a simple yet powerful mission: to make world-class healthcare accessible and affordable for everyone, regardless of their geographic location or financial constraints.

India has emerged as a global leader in medical tourism, combining ancient healing wisdom with cutting-edge medical technology. However, navigating the complexities of international medical travel—from visa requirements to hospital selection, language barriers to post-treatment care—can be overwhelming for patients seeking treatment abroad.

Recognizing this challenge, BharatHeal was established to bridge the gap between international patients and India's premier healthcare facilities. We serve as your trusted partner, handling every aspect of your medical journey with professionalism, compassion, and expertise.

Our team comprises healthcare professionals, travel coordinators, visa specialists, and patient care advocates who understand both the medical and logistical challenges of international medical travel. We've successfully facilitated medical journeys for over 50,000 patients from across the globe, building a reputation for reliability, transparency, and exceptional patient care.

At BharatHeal, we believe that quality healthcare is a fundamental right, not a privilege. By making India's world-class medical facilities accessible to international patients, we're not just facilitating medical treatment—we're transforming lives, reuniting families, and giving hope to those who need it most.`
    },
    vision: {
      title: "Our Vision",
      content: "To become the world's most trusted medical tourism platform, making world-class healthcare accessible to patients globally while maintaining the highest standards of quality, safety, and patient care."
    },
    mission: {
      title: "Our Mission",
      content: "To provide seamless, affordable, and world-class medical tourism services by connecting international patients with India's premier healthcare facilities, ensuring exceptional care, transparency, and support throughout their medical journey."
    },
    whyIndia: {
      title: "Why Choose India for Medical Treatment?",
      sections: [
        {
          title: "Cost-Effective Healthcare",
          content: "India offers world-class medical treatment at 60-80% lower costs compared to Western countries. This affordability doesn't compromise quality—patients receive the same advanced treatments, cutting-edge technology, and expert care at a fraction of the cost."
        },
        {
          title: "Internationally Accredited Hospitals",
          content: "India boasts over 50 JCI (Joint Commission International) accredited hospitals and numerous NABH (National Accreditation Board for Hospitals) certified facilities. These accreditations ensure that Indian hospitals meet the highest international quality and safety standards."
        },
        {
          title: "World-Class Medical Expertise",
          content: "Indian doctors are among the most qualified globally, with many trained at prestigious institutions in the US, UK, and Europe. With decades of experience and exposure to diverse medical cases, Indian specialists offer unmatched expertise in complex procedures."
        },
        {
          title: "Advanced Medical Technology",
          content: "Indian hospitals invest heavily in state-of-the-art medical equipment and technology. From robotic surgery systems to advanced imaging technology, patients have access to the latest medical innovations."
        },
        {
          title: "No Waiting Lists",
          content: "Unlike many Western countries where patients face long waiting periods, Indian hospitals offer prompt treatment scheduling. Most procedures can be arranged within days or weeks, not months."
        },
        {
          title: "Holistic Healthcare Approach",
          content: "India's unique combination of modern medicine with traditional healing practices like Ayurveda and Yoga offers patients comprehensive treatment options and holistic recovery approaches."
        }
      ]
    },
    bharathealAdvantage: {
      title: "The BharatHeal Advantage",
      sections: [
        {
          title: "End-to-End Support",
          content: "From initial consultation to post-treatment follow-up, we handle every aspect of your medical journey. Our comprehensive support includes visa assistance, travel coordination, hospital selection, interpreter services, and accommodation arrangements."
        },
        {
          title: "Transparent Pricing",
          content: "No hidden costs or surprise charges. We provide detailed, upfront cost estimates for all treatments, ensuring complete transparency. Our partnerships with hospitals allow us to offer competitive, all-inclusive packages."
        },
        {
          title: "Personalized Care",
          content: "Every patient receives a dedicated care coordinator who understands their unique medical needs, cultural background, and preferences. We tailor our services to ensure comfort and peace of mind throughout the journey."
        },
        {
          title: "Quality Assurance",
          content: "We partner only with internationally accredited hospitals and verified medical professionals. Our rigorous selection process ensures that patients receive care from facilities meeting the highest global standards."
        },
        {
          title: "Multilingual Support",
          content: "Our team speaks multiple languages and provides professional medical interpreters to ensure clear communication between patients and healthcare providers, eliminating language barriers."
        },
        {
          title: "24/7 Availability",
          content: "Medical emergencies don't follow business hours. Our support team is available round-the-clock to assist with any concerns, questions, or emergencies that may arise during your medical journey."
        }
      ]
    }
  },
  services: {
    title: "Medical Tourism Services | Visa, Hospital & Travel Support",
    description: "Complete medical tourism services: medical visa assistance, hospital coordination, treatment packages, travel arrangements, and post-treatment care. End-to-end support for international patients.",
    keywords: "medical visa India, hospital coordination India, medical tourism services, treatment packages India, travel medical assistance",
    services: [
      {
        title: "Medical Visa Assistance",
        description: "Complete support for obtaining your medical visa to India. Our expert team handles all documentation, visa application processing, and ensures timely approval. We provide medical visa invitation letters from recognized hospitals and guide you through every step of the visa process.",
        details: [
          "Medical visa invitation letters from accredited hospitals",
          "Complete documentation assistance and review",
          "Visa application form filling and submission support",
          "Follow-up with Indian embassies and consulates",
          "Fast-track processing for urgent medical cases",
          "Multi-entry visa support for follow-up treatments"
        ],
        icon: "FileCheck"
      },
      {
        title: "Hospital & Doctor Coordination",
        description: "We connect you with the best hospitals and doctors for your specific medical condition. Our network includes 50+ premier hospitals and 200+ expert doctors across all specialties. We handle appointment scheduling, doctor consultations, and ensure seamless coordination between all parties.",
        details: [
          "Expert hospital selection based on your medical needs",
          "Doctor matching with specialists in your condition",
          "Pre-treatment consultations and second opinions",
          "Appointment scheduling and coordination",
          "Medical report review and treatment planning",
          "Hospital admission and discharge coordination"
        ],
        icon: "Building2"
      },
      {
        title: "Comprehensive Treatment Packages",
        description: "All-inclusive treatment packages covering medical procedures, hospital stay, medications, and follow-up care. Our transparent pricing ensures no hidden costs, and we offer flexible payment options to suit your budget.",
        details: [
          "All-inclusive treatment cost estimates",
          "Transparent pricing with no hidden charges",
          "Flexible payment plans and options",
          "Package customization based on your needs",
          "Cost comparison with your home country",
          "Insurance coordination and claims support"
        ],
        icon: "DollarSign"
      },
      {
        title: "Travel & Accommodation Support",
        description: "Complete travel assistance including flight bookings, airport transfers, hotel reservations, and local transportation. We ensure comfortable accommodation near your treatment hospital and provide travel support throughout your stay.",
        details: [
          "Flight booking assistance and recommendations",
          "Complimentary airport pickup and drop-off",
          "Hotel reservations near treatment hospitals",
          "Local transportation arrangements",
          "Travel insurance guidance",
          "Sightseeing and recovery trip planning"
        ],
        icon: "Plane"
      },
      {
        title: "Post-Treatment Care & Follow-up",
        description: "Comprehensive post-treatment support including recovery monitoring, medication management, follow-up consultations, and telemedicine services. We ensure continuity of care even after you return home.",
        details: [
          "Post-treatment recovery monitoring",
          "Medication management and prescriptions",
          "Follow-up consultation scheduling",
          "Telemedicine services for remote consultations",
          "Medical report sharing with home country doctors",
          "24/7 support for post-treatment concerns"
        ],
        icon: "Heart"
      }
    ]
  },
  treatments: {
    title: "Medical Treatments in India | Cardiac, Cancer, IVF & More",
    description: "Comprehensive medical treatments in India: cardiac surgery, orthopedic surgery, cancer treatment, cosmetic surgery, organ transplant, IVF. World-class care at affordable costs.",
    keywords: "cardiac surgery India, cancer treatment India, IVF treatment India, orthopedic surgery India, cosmetic surgery India, organ transplant India",
    treatments: [
      {
        title: "Cardiac Surgery",
        slug: "cardiac-surgery",
        description: "Advanced cardiac procedures performed by world-renowned cardiologists and cardiac surgeons. India offers cutting-edge cardiac care including bypass surgery, valve replacement, angioplasty, and minimally invasive heart procedures.",
        detailedContent: `India has emerged as a global leader in cardiac surgery, with success rates matching or exceeding international standards. Our partner hospitals perform thousands of cardiac procedures annually, treating complex conditions including:

**Common Cardiac Procedures:**
- Coronary Artery Bypass Grafting (CABG)
- Heart Valve Replacement and Repair
- Angioplasty and Stenting
- Minimally Invasive Cardiac Surgery
- Heart Transplant
- Pediatric Cardiac Surgery
- Arrhythmia Treatment and Ablation

**Why Choose India for Cardiac Surgery?**
- **Cost Savings**: Save 70-80% compared to Western countries
- **Expert Surgeons**: Internationally trained cardiac surgeons with decades of experience
- **Advanced Technology**: State-of-the-art cardiac catheterization labs and surgical suites
- **High Success Rates**: 98%+ success rates for common cardiac procedures
- **Quick Scheduling**: No waiting lists, procedures scheduled within days

**Average Cost**: $8,000 - $15,000 (vs $50,000 - $100,000 in US/UK)
**Recovery Time**: 7-14 days hospital stay, 4-6 weeks total recovery`,
        icon: "Heart",
        price: "Starting from $8,000",
        duration: "7-14 days"
      },
      {
        title: "Orthopedic Surgery",
        slug: "orthopedic-surgery",
        description: "Comprehensive orthopedic care including joint replacement, spine surgery, sports medicine, and trauma surgery. India's orthopedic surgeons are globally recognized for their expertise in complex procedures.",
        detailedContent: `India offers world-class orthopedic surgery across all subspecialties. Our network includes leading orthopedic centers specializing in joint replacement, spine surgery, sports medicine, and trauma care.

**Common Orthopedic Procedures:**
- Total Hip Replacement
- Total Knee Replacement
- Shoulder Replacement
- Spine Surgery (Discectomy, Fusion, Scoliosis Correction)
- ACL Reconstruction
- Arthroscopic Surgery
- Fracture Fixation and Trauma Surgery
- Hand and Wrist Surgery

**Why Choose India for Orthopedic Surgery?**
- **Robotic-Assisted Surgery**: Advanced robotic systems for precision joint replacement
- **Minimally Invasive Techniques**: Faster recovery with smaller incisions
- **Expert Surgeons**: Fellowship-trained orthopedic surgeons
- **Comprehensive Rehabilitation**: Integrated physiotherapy and recovery programs
- **Cost-Effective**: 60-75% cost savings compared to Western countries

**Average Cost**: $5,000 - $12,000 (vs $30,000 - $60,000 in US/UK)
**Recovery Time**: 5-10 days hospital stay, 6-12 weeks rehabilitation`,
        icon: "Bone",
        price: "Starting from $5,000",
        duration: "5-10 days"
      },
      {
        title: "Cancer Treatment",
        slug: "cancer-treatment",
        description: "Comprehensive oncology care including chemotherapy, radiation therapy, surgical oncology, immunotherapy, and targeted therapy. India's cancer centers offer world-class treatment with advanced technology and expert oncologists.",
        detailedContent: `India has become a preferred destination for cancer treatment, offering comprehensive oncology care at internationally accredited cancer centers. Our partner hospitals provide multidisciplinary cancer care with advanced treatment modalities.

**Cancer Treatment Modalities:**
- Surgical Oncology (Tumor Removal)
- Chemotherapy
- Radiation Therapy (IMRT, IGRT, SBRT)
- Immunotherapy
- Targeted Therapy
- Bone Marrow Transplant
- CAR-T Cell Therapy
- Proton Therapy

**Types of Cancer Treated:**
- Breast Cancer
- Lung Cancer
- Colorectal Cancer
- Prostate Cancer
- Blood Cancers (Leukemia, Lymphoma)
- Brain Tumors
- Gynecological Cancers
- Pediatric Cancers

**Why Choose India for Cancer Treatment?**
- **Advanced Technology**: Latest radiation therapy systems and surgical robots
- **Multidisciplinary Teams**: Expert oncologists, surgeons, radiologists working together
- **Clinical Trials**: Access to latest treatment protocols and clinical trials
- **Cost-Effective**: 70-80% lower costs than Western countries
- **Holistic Care**: Integrated support services including nutrition, counseling, and rehabilitation

**Average Cost**: $6,000 - $25,000 (vs $50,000 - $200,000+ in US/UK)
**Treatment Duration**: Varies by cancer type and stage`,
        icon: "Activity",
        price: "Starting from $6,000",
        duration: "Variable"
      },
      {
        title: "Cosmetic Surgery",
        slug: "cosmetic-surgery",
        description: "Advanced aesthetic and cosmetic procedures performed by board-certified plastic surgeons. India offers world-class cosmetic surgery at affordable prices with natural-looking results.",
        detailedContent: `India has emerged as a leading destination for cosmetic surgery, combining skilled surgeons with affordable pricing. Our partner clinics offer a wide range of aesthetic procedures with excellent outcomes and natural-looking results.

**Popular Cosmetic Procedures:**
- Rhinoplasty (Nose Reshaping)
- Breast Augmentation, Reduction, and Lift
- Liposuction and Body Contouring
- Facelift and Neck Lift
- Eyelid Surgery (Blepharoplasty)
- Tummy Tuck (Abdominoplasty)
- Hair Transplant
- Dental Cosmetic Procedures

**Why Choose India for Cosmetic Surgery?**
- **Expert Surgeons**: Board-certified plastic surgeons with international training
- **Natural Results**: Focus on enhancing natural beauty, not dramatic changes
- **Affordable Pricing**: 60-70% lower costs than Western countries
- **Modern Facilities**: State-of-the-art cosmetic surgery centers
- **Privacy & Comfort**: Private recovery facilities with personalized care
- **Combined Procedures**: Many patients combine multiple procedures for comprehensive transformation

**Average Cost**: $3,000 - $8,000 (vs $10,000 - $30,000 in US/UK)
**Recovery Time**: 3-7 days hospital stay, 2-4 weeks total recovery`,
        icon: "Sparkles",
        price: "Starting from $3,000",
        duration: "3-7 days"
      },
      {
        title: "Organ Transplant",
        slug: "organ-transplant",
        description: "Life-saving organ transplant procedures including kidney, liver, heart, and bone marrow transplants. India's transplant centers have excellent success rates and follow international protocols.",
        detailedContent: `India has established itself as a leading destination for organ transplantation, with transplant centers matching international success rates. Our partner hospitals perform various organ transplant procedures following strict ethical and medical protocols.

**Organ Transplant Procedures:**
- Kidney Transplant
- Liver Transplant
- Heart Transplant
- Lung Transplant
- Bone Marrow Transplant
- Pancreas Transplant
- Cornea Transplant

**Why Choose India for Organ Transplant?**
- **High Success Rates**: 90-95% success rates for kidney and liver transplants
- **Expert Transplant Teams**: Internationally trained transplant surgeons
- **Advanced Immunosuppression**: Latest protocols for organ rejection prevention
- **Ethical Practices**: Strict adherence to international transplant guidelines
- **Post-Transplant Care**: Comprehensive follow-up and monitoring programs
- **Cost-Effective**: 70-80% lower costs than Western countries

**Important Notes:**
- Organ transplantation in India follows strict legal and ethical guidelines
- Living donor transplants are available for kidney and liver
- Cadaveric organ transplants are coordinated through authorized organ banks
- Comprehensive pre-transplant evaluation and post-transplant care included

**Average Cost**: $15,000 - $50,000 (vs $100,000 - $500,000+ in US/UK)
**Hospital Stay**: 2-4 weeks, with extended monitoring period`,
        icon: "Heart",
        price: "Starting from $15,000",
        duration: "2-4 weeks"
      },
      {
        title: "IVF Treatment",
        slug: "ivf-treatment",
        description: "Advanced fertility treatments including IVF, ICSI, IUI, and surrogacy services. India offers world-class fertility care with high success rates and affordable costs.",
        detailedContent: `India has become a global hub for fertility treatment, offering advanced reproductive technologies at a fraction of Western costs. Our partner fertility centers have excellent success rates and provide comprehensive fertility care.

**Fertility Treatment Options:**
- In Vitro Fertilization (IVF)
- Intracytoplasmic Sperm Injection (ICSI)
- Intrauterine Insemination (IUI)
- Egg Freezing and Sperm Freezing
- Preimplantation Genetic Testing (PGT)
- Surrogacy Services
- Donor Egg and Sperm Programs

**Why Choose India for IVF Treatment?**
- **High Success Rates**: 50-65% success rates for women under 35
- **Advanced Technology**: Latest IVF lab equipment and techniques
- **Expert Fertility Specialists**: Internationally trained reproductive endocrinologists
- **Affordable Costs**: 70-80% lower than Western countries
- **Comprehensive Care**: Full-cycle treatment including medications and monitoring
- **Legal Surrogacy**: Regulated surrogacy programs for eligible couples

**IVF Process:**
1. Initial consultation and fertility assessment
2. Ovarian stimulation and monitoring
3. Egg retrieval procedure
4. Fertilization in advanced laboratory
5. Embryo transfer
6. Pregnancy testing and early pregnancy care

**Average Cost**: $4,000 - $6,000 per cycle (vs $15,000 - $25,000 in US/UK)
**Treatment Duration**: 2-3 weeks per cycle`,
        icon: "Baby",
        price: "Starting from $4,000",
        duration: "2-3 weeks"
      }
    ]
  },
  blog: {
    title: "Medical Tourism Blog | Healthcare Updates & Guides | BharatHeal",
    description: "Stay updated with the latest in medical tourism, treatment costs, and healthcare in India. Expert guides on medical visas, surgeries, and patient stories.",
    keywords: "medical tourism blog, healthcare India blog, medical travel guide, surgery cost India 2026, patient success stories",
  },

  // Per-page metadata for the remaining public routes. Without these, each of
  // these pages inherited the homepage title and description, so they competed
  // with each other in search results instead of ranking for their own terms.
  surgery: {
    title: "Affordable Surgery in India | Cost & Hospitals | BharatHeal",
    description: "Compare surgery costs in India and save 60-80% versus Western prices. Cardiac, orthopaedic, oncology and transplant procedures at accredited hospitals.",
    canonical: "/treatments/surgery",
  },
  ayurveda: {
    title: "Ayurveda Treatment in India | Authentic Centres | BharatHeal",
    description: "Authentic Ayurvedic treatment and Panchakarma at accredited centres in India, with full support for international patients from arrival to follow-up.",
    canonical: "/treatments/ayurveda",
  },
  yoga: {
    title: "Yoga Therapy in India for International Patients | BharatHeal",
    description: "Therapeutic yoga programmes in India designed around your medical condition, delivered by certified practitioners alongside your treatment plan.",
    canonical: "/treatments/yoga",
  },
  consultation: {
    title: "Online Medical Consultation with Indian Doctors | BharatHeal",
    description: "Consult experienced Indian specialists online before you travel. Get a treatment plan, cost estimate and second opinion without leaving home.",
    canonical: "/treatments/consultation",
  },
  contact: {
    title: "Contact BharatHeal | Medical Tourism Enquiries",
    description: "Talk to our international patient team about treatment in India, hospital selection, cost estimates and medical visa assistance.",
    canonical: "/contact",
  },
  freeConsultation: {
    title: "Free Medical Consultation | Treatment in India | BharatHeal",
    description: "Request a free consultation. Share your medical reports and receive a treatment plan, hospital recommendation and cost estimate at no charge.",
    canonical: "/free-consultation",
  },
  patientJourney: {
    title: "Your Patient Journey | Treatment in India Step by Step | BharatHeal",
    description: "See exactly how treatment in India works, from first enquiry and medical visa through hospital admission, surgery and post-treatment follow-up.",
    canonical: "/patient-journey",
  },
};
